import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { IS_APP_CODEAC, PUBLIC_URL } from '../../../constants/env';
import { withPlanInfo } from '../../../components/hoc/WithPlanInfo';
import { withHandleActivateTrial } from '../../../components/hoc/WithHandleActivateTrial';
const LANGUAGES_IN_BREAKDOWN = 11; // 10 + other
const MAX_MESSAGE_LENGTH = 40;
export class LanguageBreakdown extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            displayLanguages: [],
            displayLanguagesLastCommit: [],
            langArray: [],
            otherPercent: 0,
            commitStatuses: {},
            windowWidth: 2490,
        };
        this.onChangeHandler = (payload) => {
            const { displayLanguages } = this.state;
            const displayLanguagesUpdatedPercent = displayLanguages.map((language) => {
                const payloadMatch = payload.find((lang) => lang.name === language.name);
                // @ts-expect-error TS18048: payloadMatch is possibly undefined
                const changedPercent = payloadMatch.payload._tooltip[payloadMatch.name];
                return { ...language, percent: changedPercent };
            });
            if (displayLanguagesUpdatedPercent.length) {
                // @ts-expect-error TS2322: Type { percent: any; }[] is not assignable to type
                this.setState({ displayLanguages: displayLanguagesUpdatedPercent });
            }
        };
    }
    componentDidMount() {
        this.getLangData();
        window.addEventListener('resize', () => {
            this.setState({
                windowWidth: window.innerWidth,
            });
        });
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const activeBranch = nextProps.activeBranch;
        const commitStatuses = Object.assign({}, ...(activeBranch.commitList && activeBranch.commitList.commits.map((commit) => ({
            [commit.id]: {
                status: commit.status,
                languagesDetected: !!commit.languages,
            },
        }))));
        if (JSON.stringify(commitStatuses) !== JSON.stringify(this.state.commitStatuses)) {
            this.setState({
                displayLanguages: [],
                displayLanguagesLastCommit: [],
                langArray: [],
                otherPercent: 0,
                commitStatuses: commitStatuses,
            }, this.getLangData);
        }
    }
    getLangPercent(fileStats, lang) {
        if (lang === 'Other') {
            return this.state.otherPercent;
        }
        if (!fileStats) {
            return 0;
        }
        const fileLanguage = fileStats.languages.find((language) => language.name === lang);
        if (!fileLanguage) {
            return 0;
        }
        const percent = (fileLanguage.loc / fileStats.loc) * 100;
        return Math.floor(percent * 100) / 100;
    }
    getLastCommitLanguages(otherPlaceholder, displayedLangs) {
        const { activeBranch } = this.props;
        const lastCommit = activeBranch.getLastCommit();
        return otherPlaceholder.percent > 0 && lastCommit.isDone() ? [...displayedLangs, otherPlaceholder] : displayedLangs;
    }
    getLangData() {
        const { activeBranch } = this.props;
        const languageBreakdown = activeBranch.commitList.getLanguageBreakdown(activeBranch.getLastCommit(), this.getLangPercent);
        const displayLanguagesLastCommit = this.getLastCommitLanguages(languageBreakdown.otherPlaceholder, languageBreakdown.displayedLangs);
        return this.setState({
            langArray: languageBreakdown.langArray,
            displayLanguages: displayLanguagesLastCommit,
            displayLanguagesLastCommit,
            otherPercent: languageBreakdown.otherPlaceholder.percent,
        });
    }
    getTimeline() {
        const { activeBranch } = this.props;
        const { langArray } = this.state;
        const filteredLists = activeBranch.commitList.commits.filter((commit) => commit.message);
        return filteredLists.map((commit) => {
            const langHistoryStats = {};
            langArray.forEach((lang) => (langHistoryStats[lang.name] = this.getLangPercent(commit.languages, lang.name)));
            const langsSorted = Object.entries(langHistoryStats).sort((lang1, lang2) => parseFloat(lang2[1]) - parseFloat(lang1[1]));
            const otherInFirstPlaces = langsSorted.slice(0, LANGUAGES_IN_BREAKDOWN).filter((lng) => lng[0] === 'Other').length > 0;
            const displayedLanguages = langsSorted.slice(0, LANGUAGES_IN_BREAKDOWN + (otherInFirstPlaces ? 1 : 0));
            const displayedLangsToObject = {};
            for (let lang of displayedLanguages) {
                displayedLangsToObject[lang[0]] = lang[1];
            }
            const langsToSum = JSON.parse(JSON.stringify(displayedLangsToObject));
            if (Object.prototype.hasOwnProperty.call(langsToSum, 'Other')) {
                delete langsToSum.Other;
            }
            const otherPlaceholder = {
                name: 'Other',
                percent: Math.floor(
                // @ts-expect-error TS2571: Object is of type unknown
                100 * (100 - Object.values(langsToSum).reduce((acc, val) => acc + parseFloat(val), 0))) / 100,
                color: '#f3f3f3', // @ToDo: Use a constant for the color
            }, tooltip = {
                ...JSON.parse(JSON.stringify(langsToSum)),
                Other: Number(
                // @ts-expect-error TS2571: Object is of type unknown
                (100 - Object.values(langsToSum).reduce((acc, val) => acc + parseFloat(val), 0)).toFixed(2)),
            };
            displayedLangsToObject[otherPlaceholder.name] = otherPlaceholder.percent;
            // increase values 100x due to floating point representation in JS (otherwise sum can be 100.0000000000001)
            Object.keys(displayedLangsToObject).forEach((key) => (displayedLangsToObject[key] *= 100));
            return {
                name: format(new Date(Number(commit.datetime)), 'MM/dd hh:mm'),
                message: commit.message.length > MAX_MESSAGE_LENGTH
                    ? commit.message.substr(0, MAX_MESSAGE_LENGTH) + '…'
                    : commit.message,
                ...displayedLangsToObject,
                _tooltip: tooltip,
            };
        });
    }
    render() {
        const { t, repository, proPlan, trialGraph, trialActivateable, activeBranch, handleActivateTrial } = this.props;
        const { langArray, displayLanguages, displayLanguagesLastCommit } = this.state;
        const timeline = this.getTimeline();
        const showTrialContent = IS_APP_CODEAC && !proPlan && repository.private && trialGraph;
        const handleOnMouseOut = () => {
            this.setState({ displayLanguages: displayLanguagesLastCommit });
        };
        const commits = activeBranch.commitList.commits;
        if (langArray.length > 0) {
            return (_jsx("div", { className: "container language-breakdown", children: _jsx("div", { className: "row", children: _jsx("div", { className: "col mb-12", children: _jsx("div", { className: "card h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("h3", { className: "h5", children: t('languageBreakdown') }), !showTrialContent && (_jsx("div", { className: "language-breakdown__wrapper", children: displayLanguages.map((language) => (_jsxs("div", { className: "language-breakdown__wrapper-single", children: [_jsx("div", { className: "language-breakdown__wrapper-single-title-border", style: {
                                                        background: language.color,
                                                    } }), _jsx("div", { className: "language-breakdown__wrapper-single-title", children: language.name }), _jsx("div", { className: "language-breakdown__wrapper-single-percent", children: !language.percent ? ('-') : (_jsxs("span", { children: [language.percent, "\u00A0%"] })) })] }, language.name))) })), showTrialContent ? (_jsxs("div", { className: "language-breakdown-graph", children: [_jsx("div", { className: "more-in-pro", children: trialActivateable ? (_jsx(Button, { color: "outline-secondary", size: "md", className: "btn-activate-trial", onClick: handleActivateTrial, children: t('settings.tryPro', {
                                                        siteName: t('siteName'),
                                                    }) })) : (_jsx(Link, { to: "/settings/billing", className: "btn btn-outline-secondary btn-activate-trial", children: t('availableInPro') })) }), _jsx("img", { className: "grayscale-graph", src: `${PUBLIC_URL}images/language-breakdown.jpg`, srcSet: `${PUBLIC_URL}images/language-breakdown.jpg 1x,
													${PUBLIC_URL}images/language-breakdown@2x.jpg 2x`, alt: `${t('trialLBGraph')}`, width: "100%" })] })) : !commits.length ? _jsx("p", { children: t('noChartData') }) : null, !showTrialContent && commits.length > 1 && _jsx(ResponsiveContainer, { width: "100%", height: 280, children: _jsxs(AreaChart, { onMouseOut: handleOnMouseOut, width: 600, height: 400, data: timeline.reverse(), margin: { top: 10, right: 30, left: 0, bottom: 0 }, onMouseMove: (event) => {
                                                if (event && event.activePayload) {
                                                    this.onChangeHandler(event.activePayload);
                                                }
                                            }, children: [_jsx(XAxis, { dataKey: "name", interval: "preserveStartEnd" }), _jsx(Tooltip, { content: () => null }), langArray.map((language, i) => {
                                                    return i >= 20 ? null : (_jsx(Area, { type: "monotone", dataKey: language.name, stackId: "1", stroke: language.color, fill: language.color }, language.name));
                                                })] }) })] }) }) }) }) }));
        }
        return null;
    }
}
LanguageBreakdown.defaultProps = {
    trialGraph: false,
};
export default withTranslation()(withHandleActivateTrial(withPlanInfo(LanguageBreakdown)));
